import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AppSettingsService } from '@core';
import moment from 'moment';

@Injectable()
export class DateFormattingInterceptor implements HttpInterceptor {

  // Matches an ISO 8601 datetime carrying an explicit timezone: trailing Z (UTC)
  private static readonly ISO_DATETIME_TZ_RE =
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{1,9})?(?:Z|[+-]\d{2}:?\d{2})$/;

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Only process requests with a body (POST, PUT, PATCH)
    if (!request.body) {
      return next.handle(request);
    }

    // Check if this is a v2.0 endpoint
    const v2BaseUrl = AppSettingsService.appSettings.edaraApi.baseUrl + 'v2.0/';
    if (!request.url.startsWith(v2BaseUrl)) {
      return next.handle(request);
    }

    // Transform dates in the request body
    const transformedBody = this.transformDates(request.body);

    // Clone the request with the transformed body
    const transformedRequest = request.clone({
      body: transformedBody
    });

    return next.handle(transformedRequest);
  }

  /**
   * Recursively normalizes Date / Moment / TZ-qualified ISO strings in the body
   * to the local ISO-with-offset form the backend expects (Egypt local time).
   */
  private transformDates(obj: any): any {
    if (obj === null || obj === undefined) {
      return obj;
    }

    // Handle Moment objects (Material's MomentDateAdapter produces these)
    if (moment.isMoment(obj)) {
      return obj.format();
    }

    // Handle Date objects
    if (obj instanceof Date) {
      return moment(obj).format();
    }

    // Handle ISO 8601 datetime strings with an explicit timezone marker.
    if (typeof obj === 'string' && DateFormattingInterceptor.ISO_DATETIME_TZ_RE.test(obj)) {
      return moment(obj).format();
    }

    // Handle Arrays
    if (Array.isArray(obj)) {
      return obj.map(item => this.transformDates(item));
    }

    // Handle Objects
    if (typeof obj === 'object') {
      const transformed: any = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          transformed[key] = this.transformDates(obj[key]);
        }
      }
      return transformed;
    }

    // Return primitives as-is
    return obj;
  }
}
